/**
 * Swipe-to-go-back Gesture Hook
 *
 * Implements iOS-style swipe-from-edge gesture for back navigation.
 * Only activates when swiping from the left edge of the screen.
 *
 * @module useSwipeBack
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';

/**
 * Configuration options for swipe back gesture
 */
interface SwipeBackOptions {
  /** Enable/disable the gesture (default: true) */
  enabled?: boolean;
  /** Edge zone width in pixels to trigger gesture (default: 30) */
  edgeZone?: number;
  /** Minimum swipe distance to trigger navigation (default: 100) */
  threshold?: number;
  /** Callback when navigation is triggered */
  onNavigateBack?: () => void;
  /** Routes that should not support back navigation */
  excludedRoutes?: string[];
}

/**
 * Swipe back state
 */
interface SwipeBackState {
  /** Whether a swipe gesture is in progress */
  isSwiping: boolean;
  /** Current swipe offset (0 to screen width) */
  swipeOffset: number;
  /** Progress of swipe (0 to 1) */
  swipeProgress: number;
}

/**
 * Return type for useSwipeBack hook
 */
interface UseSwipeBackReturn extends SwipeBackState {
  /** Handlers to attach to the swipe container */
  handlers: {
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchMove: (e: React.TouchEvent) => void;
    onTouchEnd: (e: React.TouchEvent) => void;
  };
  /** Style to apply to the content being swiped */
  swipeStyle: React.CSSProperties;
  /** Whether back navigation is available */
  canGoBack: boolean;
}

/**
 * Hook for swipe-to-go-back gesture
 */
export function useSwipeBack(options: SwipeBackOptions = {}): UseSwipeBackReturn {
  const {
    enabled = true,
    edgeZone = 30,
    threshold = 100,
    onNavigateBack,
    // '/signup' stays even though the page is gone: the route still renders a
    // redirect at that path, and swiping back into it mid-redirect is a dead
    // auth loop. Pinned by SignupPathReferencesRetained.test.ts.
    excludedRoutes = ['/dashboard', '/login', '/signup', '/'],
  } = options;

  const navigate = useNavigate();
  const location = useLocation();
  const isNativePlatform = Capacitor.isNativePlatform();

  // State
  const [state, setState] = useState<SwipeBackState>({
    isSwiping: false,
    swipeOffset: 0,
    swipeProgress: 0,
  });

  // Refs for tracking touch
  const startXRef = useRef<number>(0);
  const startYRef = useRef<number>(0);
  const isEdgeSwipeRef = useRef<boolean>(false);
  const hasNavigatedRef = useRef<boolean>(false);

  // Check if we can go back (not on excluded routes and has history)
  const canGoBack = !excludedRoutes.includes(location.pathname) && window.history.length > 1;

  // Reset state when location changes
  useEffect(() => {
    hasNavigatedRef.current = false;
    setState({
      isSwiping: false,
      swipeOffset: 0,
      swipeProgress: 0,
    });
  }, [location.pathname]);

  /**
   * Handle touch start
   */
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!enabled || !isNativePlatform || !canGoBack) return;

    const touch = e.touches[0];
    startXRef.current = touch.clientX;
    startYRef.current = touch.clientY;

    // Only trigger if starting from the left edge
    isEdgeSwipeRef.current = touch.clientX <= edgeZone;
  }, [enabled, isNativePlatform, canGoBack, edgeZone]);

  /**
   * Handle touch move
   */
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!enabled || !isNativePlatform || !canGoBack || !isEdgeSwipeRef.current) return;

    const touch = e.touches[0];
    const deltaX = touch.clientX - startXRef.current;
    const deltaY = touch.clientY - startYRef.current;

    // Only proceed if horizontal swipe is dominant
    if (Math.abs(deltaY) > Math.abs(deltaX) * 0.5) {
      isEdgeSwipeRef.current = false;
      setState(prev => ({
        ...prev,
        isSwiping: false,
        swipeOffset: 0,
        swipeProgress: 0,
      }));
      return;
    }

    // Only allow right swipe (positive deltaX)
    if (deltaX > 0) {
      const maxSwipe = window.innerWidth;
      const offset = Math.min(deltaX, maxSwipe);
      const progress = offset / maxSwipe;

      setState({
        isSwiping: true,
        swipeOffset: offset,
        swipeProgress: progress,
      });
    }
  }, [enabled, isNativePlatform, canGoBack]);

  /**
   * Handle touch end
   */
  const handleTouchEnd = useCallback((_e: React.TouchEvent) => {
    if (!enabled || !isNativePlatform || !canGoBack || !isEdgeSwipeRef.current) return;

    const { swipeOffset } = state;

    // Check if swipe exceeded threshold
    if (swipeOffset >= threshold && !hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      onNavigateBack?.();
      navigate(-1);
    }

    // Reset state with animation
    isEdgeSwipeRef.current = false;
    setState({
      isSwiping: false,
      swipeOffset: 0,
      swipeProgress: 0,
    });
  }, [enabled, isNativePlatform, canGoBack, state, threshold, onNavigateBack, navigate]);

  /**
   * Style for the content being swiped
   */
  const swipeStyle: React.CSSProperties = state.isSwiping
    ? {
        transform: `translateX(${state.swipeOffset}px)`,
        transition: 'none',
      }
    : {
        transform: 'translateX(0)',
        transition: 'transform 0.3s ease-out',
      };

  return {
    ...state,
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    swipeStyle,
    canGoBack,
  };
}

export default useSwipeBack;
